import {AttrsSchema, Component} from 'panel';

import {ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {formatText} from 'common/util/format-text';

import {
  ActionEvent,
  BannerAction,
  BannerAttrs,
  BannerJsxProps,
  BannerSize,
  BannerTheme,
  BannerType,
  CallToAction,
} from './types';
import {template} from './template';
import css from './index.styl?inline';

const iconFromTheme = new Map<BannerTheme, string>([
  [BannerTheme.Error, `warning`],
  [BannerTheme.Info, `info`],
  [BannerTheme.Success, `circle-checkmark`],
  [BannerTheme.Warn, `warning`],
]);

const buttonTypeFromTheme = new Map<BannerTheme, ButtonType>([
  [BannerTheme.Error, ButtonType.Destructive],
  [BannerTheme.Info, ButtonType.Info],
  [BannerTheme.Success, ButtonType.Success],
  [BannerTheme.Warn, ButtonType.Warn],
]);

export class Banner extends Component<EmptyObj, BannerAttrs> {
  static get attrsSchema(): AttrsSchema<BannerAttrs> {
    return {
      ...super.attrsSchema,
      'body-text': `string`,
      ctas: `json`,
      'header-text': `string`,
      icon: {type: `string`, default: `from-theme`},
      size: {type: `string`, enum: Object.values(BannerSize), default: BannerSize.Medium},
      theme: {type: `string`, enum: Object.values(BannerTheme), default: BannerTheme.Info},
      type: {type: `string`, enum: Object.values(BannerType), default: BannerType.Floating},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {},
      helpers: {
        BannerAction,
        BannerSize,
        BannerType,
        formatText,
        getIcon: () =>
          this.attr(`icon`) === `from-theme` ? iconFromTheme.get(this.attr(`theme`) as BannerTheme) : this.attr(`icon`),
        getButtonType: (): ButtonType => buttonTypeFromTheme.get(this.attr(`theme`) as BannerTheme),
        handleCtaButtonClick: (cta: CallToAction): void => {
          const {actionName, href, hrefTarget, trackEventName, trackProps} = cta;
          if (actionName && actionName !== BannerAction.None) {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {actionName}}) as ActionEvent);
          }

          if (trackEventName) {
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: trackEventName,
                  props: trackProps || {
                    'Action Name': actionName,
                  },
                },
              }),
            );
          }

          // if user wishes to make a link button
          if (href) {
            window.open(href, hrefTarget || `_blank`);
          }
        },
      },
    };
  }
}

export const BannerEl = defineAndMakeJsxFactory<BannerJsxProps>(`mp-banner`, Banner);
